import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert24Regular,
  Alert24Filled,
  CheckmarkCircle20Regular,
  DismissCircle20Regular,
  PersonArrowRight20Regular,
} from "@fluentui/react-icons";
import { useNotifications } from "../../context/NotificationContext";
import { useShell } from "../../context/ShellContext";
import type { NotificationDto } from "../../../api/notifications";
import "./NotificationBell.css";

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function NotificationIcon({ type }: { type: string }) {
  switch (type) {
    case "AccessRequested":
      return (
        <div className="notification-icon request">
          <PersonArrowRight20Regular />
        </div>
      );
    case "AccessApproved":
      return (
        <div className="notification-icon approved">
          <CheckmarkCircle20Regular />
        </div>
      );
    case "AccessRejected":
      return (
        <div className="notification-icon rejected">
          <DismissCircle20Regular />
        </div>
      );
    default:
      return (
        <div className="notification-icon request">
          <Alert24Regular />
        </div>
      );
  }
}

function NotificationItem({
  notification,
  onMarkRead,
  onApprove,
  onReject,
  isAdmin,
}: {
  notification: NotificationDto;
  onMarkRead: (id: string) => void;
  onApprove?: (requestId: string) => void;
  onReject?: (requestId: string) => void;
  isAdmin: boolean;
}) {
  const [acting, setActing] = useState(false);

  const handleApprove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!notification.relatedEntityId || !onApprove) return;
    setActing(true);
    try {
      await onApprove(notification.relatedEntityId);
      onMarkRead(notification.id);
    } finally {
      setActing(false);
    }
  };

  const handleReject = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!notification.relatedEntityId || !onReject) return;
    setActing(true);
    try {
      await onReject(notification.relatedEntityId);
      onMarkRead(notification.id);
    } finally {
      setActing(false);
    }
  };

  const showActions =
    isAdmin && notification.type === "AccessRequested" && !notification.isRead;

  return (
    <div
      className={`notification-item ${notification.isRead ? "" : "unread"}`}
      onClick={() => !notification.isRead && onMarkRead(notification.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !notification.isRead) onMarkRead(notification.id);
      }}
    >
      <NotificationIcon type={notification.type} />
      <div className="notification-content">
        <p className="notification-title">{notification.title}</p>
        <p className="notification-message">{notification.message}</p>
        {showActions && (
          <div className="notification-actions">
            <button className="btn-approve" disabled={acting} onClick={handleApprove}>
              Approve
            </button>
            <button className="btn-reject" disabled={acting} onClick={handleReject}>
              Reject
            </button>
          </div>
        )}
        <span className="notification-time">{timeAgo(notification.createdAt)}</span>
      </div>
      {!notification.isRead && <span className="unread-dot" />}
    </div>
  );
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { isAdmin } = useShell();
  const {
    notifications,
    unreadCount,
    markRead,
    markAllRead,
    clearAll,
    refreshNotifications,
    approveRequest,
    rejectRequest,
    refreshPendingRequests,
  } = useNotifications();

  const handleToggle = useCallback(() => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen) {
      refreshNotifications();
      if (isAdmin) refreshPendingRequests();
    }
  }, [open, refreshNotifications, isAdmin, refreshPendingRequests]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="notification-bell" ref={dropdownRef}>
      <button
        className="notification-bell-btn"
        title="Notifications"
        onClick={handleToggle}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {open ? <Alert24Filled /> : <Alert24Regular />}
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="notification-dropdown" role="menu">
          <div className="notification-dropdown-header">
            <h3 className="notification-dropdown-title">Notifications</h3>
            <div className="notification-header-actions">
              {unreadCount > 0 && (
                <button
                  className="notification-mark-all-btn"
                  onClick={() => markAllRead()}
                >
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  className="notification-clear-all-btn"
                  onClick={() => clearAll()}
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="notification-empty">
                <div className="notification-empty-icon">
                  <Alert24Regular />
                </div>
                <p>No notifications</p>
              </div>
            ) : (
              notifications.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onMarkRead={markRead}
                  onApprove={isAdmin ? approveRequest : undefined}
                  onReject={isAdmin ? rejectRequest : undefined}
                  isAdmin={isAdmin}
                />
              ))
            )}
          </div>

          {isAdmin && (
            <div className="notification-dropdown-footer">
              <button
                onClick={() => {
                  navigate("/access-requests");
                  setOpen(false);
                }}
              >
                View All Requests
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
